import { useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { notify } from "@/stores/notificationStore";
import {
  Upload,
  FileSpreadsheet,
  Download,
  AlertCircle,
  CheckCircle,
  XCircle,
  Loader2,
  ChevronRight,
  ChevronLeft,
  Trash2,
  Play,
  FileText,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { articlesApi, sitesApi } from "@/services/api";
import type { Site, ArticleGenerateRequest } from "@/types";
import { cn } from "@/lib/utils";

interface ParsedRow {
  rowNumber: number;
  topic: string;
  site_id: string;
  site_name?: string;
  keywords: string[];
  target_word_count: number;
  tone: string;
  language: string;
  include_image: boolean;
  errors: string[];
  isValid: boolean;
}

interface BatchProgress {
  total: number;
  completed: number;
  failed: number;
  inProgress: number;
  results: { rowNumber: number; articleId?: string; error?: string }[];
}

type WizardStep = "upload" | "preview" | "submit" | "progress";

const CSV_TEMPLATE = `topic,site_id,keywords,target_word_count,tone,language,include_image,include_h1,auto_publish,reference_urls
"Best Practices for Cloud Migration",site-uuid-here,"cloud,migration,enterprise",800,professional,en,true,false,false,
"SEO Tips for Small Businesses",site-uuid-here,"seo,small business,marketing",800,friendly,en,true,false,true,"https://moz.com/beginners-guide-to-seo|https://youtu.be/DvwS7cV9GmQ"
"Introduction to Machine Learning",site-uuid-here,"machine learning,ai,tutorial",1000,casual,en,false,false,false,"https://scikit-learn.org/"`;

export function BatchUploadPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<WizardStep>("upload");
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [batchPriority, setBatchPriority] = useState(3);
  const [batchProgress, setBatchProgress] = useState<BatchProgress | null>(
    null,
  );
  const [isDragging, setIsDragging] = useState(false);

  // Fetch sites for validation
  const { data: sitesData } = useQuery({
    queryKey: ["sites"],
    queryFn: () => sitesApi.list(),
  });

  const sites = useMemo(() => sitesData?.items || [], [sitesData?.items]);
  const siteMap = useMemo(
    () => new Map(sites.map((s: Site) => [s.id, s.name])),
    [sites],
  );

  // Generate article mutation
  const generateMutation = useMutation({
    mutationFn: articlesApi.generate,
  });

  // Parse CSV content
  const parseCSV = useCallback(
    (content: string): ParsedRow[] => {
      const lines = content.trim().split("\n");
      if (lines.length < 2) return [];

      const headerLine = lines[0];
      if (!headerLine) return [];

      const headers = headerLine
        .split(",")
        .map((h) => h.trim().toLowerCase().replace(/"/g, ""));
      const rows: ParsedRow[] = [];

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i];
        if (!line) continue;

        const values = parseCSVLine(line);
        const errors: string[] = [];

        // Map values to object
        const rowData: Record<string, string> = {};
        headers.forEach((header, idx) => {
          rowData[header] = values[idx]?.trim().replace(/^"|"$/g, "") || "";
        });

        // Validate required fields
        const siteId = rowData.site_id || "";
        if (!rowData.topic) errors.push("Topic is required");
        if (!siteId) errors.push("Site ID is required");
        else if (!siteMap.has(siteId)) errors.push("Invalid site ID");

        // Parse keywords
        const keywords = rowData.keywords
          ? rowData.keywords
              .split(",")
              .map((k) => k.trim())
              .filter(Boolean)
          : [];

        // Parse word count
        const targetWordCount =
          parseInt(rowData.target_word_count || "1500") || 1500;
        if (targetWordCount < 500)
          errors.push("Word count must be at least 500");
        if (targetWordCount > 5000)
          errors.push("Word count must be at most 5000");

        // Parse tone
        const validTones = ["professional", "casual", "formal", "friendly"];
        const tone = (rowData.tone || "professional").toLowerCase();
        if (!validTones.includes(tone))
          errors.push(`Invalid tone. Use: ${validTones.join(", ")}`);

        // Parse language
        const validLanguages = ["en", "he"];
        const language = (rowData.language || "en").toLowerCase();
        if (!validLanguages.includes(language))
          errors.push(`Invalid language. Use: ${validLanguages.join(", ")}`);

        // Parse include_image
        const includeImage =
          (rowData.include_image || "true").toLowerCase() !== "false";

        rows.push({
          rowNumber: i,
          topic: rowData.topic || "",
          site_id: siteId,
          site_name: siteMap.get(siteId),
          keywords,
          target_word_count: targetWordCount,
          tone,
          language,
          include_image: includeImage,
          errors,
          isValid: errors.length === 0,
        });
      }

      return rows;
    },
    [siteMap],
  );

  // Parse a single CSV line handling quoted values
  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === "," && !inQuotes) {
        result.push(current);
        current = "";
      } else {
        current += char;
      }
    }
    result.push(current);
    return result;
  };

  // Handle file drop/upload
  const handleFile = useCallback(
    (file: File) => {
      if (!file.name.endsWith(".csv")) {
        alert("Please upload a CSV file");
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        const rows = parseCSV(content);
        setParsedRows(rows);
        if (rows.length > 0) {
          setCurrentStep("preview");
        }
      };
      reader.readAsText(file);
    },
    [parseCSV],
  );

  // Handle drag events
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  // Download template
  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "batch-upload-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  // Download error report
  const downloadErrorReport = () => {
    const errorRows = parsedRows.filter((r) => !r.isValid);
    const report = errorRows.map((r) => ({
      row: r.rowNumber,
      topic: r.topic,
      errors: r.errors.join("; "),
    }));
    const blob = new Blob([JSON.stringify(report, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "batch-upload-errors.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  // Remove row
  const removeRow = (rowNumber: number) => {
    setParsedRows((prev) => prev.filter((r) => r.rowNumber !== rowNumber));
  };

  // Submit batch
  const submitBatch = async () => {
    const validRows = parsedRows.filter((r) => r.isValid);
    if (validRows.length === 0) return;

    setCurrentStep("progress");
    setBatchProgress({
      total: validRows.length,
      completed: 0,
      failed: 0,
      inProgress: 0,
      results: [],
    });

    for (const row of validRows) {
      setBatchProgress((prev) =>
        prev ? { ...prev, inProgress: prev.inProgress + 1 } : prev,
      );

      try {
        const request: ArticleGenerateRequest = {
          site_id: row.site_id,
          topic: row.topic,
          keywords: row.keywords,
          target_word_count: row.target_word_count,
          tone: row.tone as ArticleGenerateRequest["tone"],
          language: row.language as ArticleGenerateRequest["language"],
          include_image: row.include_image,
        };

        const result = await generateMutation.mutateAsync(request);

        setBatchProgress((prev) =>
          prev
            ? {
                ...prev,
                completed: prev.completed + 1,
                inProgress: prev.inProgress - 1,
                results: [
                  ...prev.results,
                  { rowNumber: row.rowNumber, articleId: result.id },
                ],
              }
            : prev,
        );
      } catch (error) {
        setBatchProgress((prev) =>
          prev
            ? {
                ...prev,
                failed: prev.failed + 1,
                inProgress: prev.inProgress - 1,
                results: [
                  ...prev.results,
                  { rowNumber: row.rowNumber, error: (error as Error).message },
                ],
              }
            : prev,
        );
      }

      // Small delay between requests to avoid overwhelming the API
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
  };

  // Stats
  const validCount = parsedRows.filter((r) => r.isValid).length;
  const invalidCount = parsedRows.filter((r) => !r.isValid).length;
  const progressPercent = batchProgress
    ? ((batchProgress.completed + batchProgress.failed) / batchProgress.total) *
      100
    : 0;

  // Send notification when batch processing completes
  useEffect(() => {
    if (
      batchProgress &&
      batchProgress.total > 0 &&
      batchProgress.completed + batchProgress.failed === batchProgress.total
    ) {
      notify.batchComplete(batchProgress.total, batchProgress.failed);
    }
  }, [batchProgress]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {t("batchUpload.title")}
          </h1>
          <p className="text-muted-foreground">
            {t("batchUpload.description")}
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate("/articles")}>
          {t("batchUpload.backToArticles")}
        </Button>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center gap-2">
        {(["upload", "preview", "submit", "progress"] as WizardStep[]).map(
          (step, idx) => (
            <div key={step} className="flex items-center">
              <div
                className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium",
                  currentStep === step
                    ? "bg-primary text-primary-foreground"
                    : idx <
                        ["upload", "preview", "submit", "progress"].indexOf(
                          currentStep,
                        )
                      ? "bg-green-500 text-white"
                      : "bg-muted text-muted-foreground",
                )}
              >
                {idx + 1}
              </div>
              {idx < 3 && (
                <ChevronRight className="h-4 w-4 mx-2 text-muted-foreground" />
              )}
            </div>
          ),
        )}
      </div>

      {/* Upload Step */}
      {currentStep === "upload" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              {t("batchUpload.upload.title")}
            </CardTitle>
            <CardDescription>
              {t("batchUpload.upload.description")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Drop Zone */}
            <div
              className={cn(
                "border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors",
                isDragging
                  ? "border-primary bg-primary/5"
                  : "border-muted-foreground/25 hover:border-primary/50",
              )}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById("csv-upload")?.click()}
            >
              <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-lg font-medium mb-1">
                {t("batchUpload.upload.dropHere")}
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                {t("batchUpload.upload.orClickBrowse")}
              </p>
              <input
                id="csv-upload"
                type="file"
                accept=".csv"
                className="hidden"
                onChange={(e) =>
                  e.target.files?.[0] && handleFile(e.target.files[0])
                }
              />
              <Button variant="outline" size="sm">
                {t("batchUpload.upload.selectFile")}
              </Button>
            </div>

            {/* Template Download */}
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <p className="font-medium">
                  {t("batchUpload.template.needTemplate")}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("batchUpload.template.downloadDescription")}
                </p>
              </div>
              <Button variant="outline" onClick={downloadTemplate}>
                <Download className="me-2 h-4 w-4" />
                {t("batchUpload.template.download")}
              </Button>
            </div>

            {/* Format Instructions */}
            <div className="space-y-2">
              <h3 className="font-medium">{t("batchUpload.format.title")}</h3>
              <div className="text-sm text-muted-foreground space-y-1">
                <p>
                  <strong>{t("batchUpload.format.requiredColumns")}:</strong>{" "}
                  topic, site_id
                </p>
                <p>
                  <strong>{t("batchUpload.format.optionalColumns")}:</strong>{" "}
                  keywords, target_word_count, tone, language, include_image
                </p>
                <p>
                  <strong>{t("batchUpload.format.toneOptions")}:</strong>{" "}
                  professional, casual, formal, friendly
                </p>
                <p>
                  <strong>{t("batchUpload.format.languageOptions")}:</strong>{" "}
                  en, he
                </p>
              </div>
            </div>

            {/* Available Sites */}
            {sites.length > 0 && (
              <div className="space-y-2">
                <h3 className="font-medium">
                  {t("batchUpload.sites.available")}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {sites.map((site: Site) => (
                    <div
                      key={site.id}
                      className="text-xs bg-muted px-2 py-1 rounded"
                    >
                      <span className="font-mono">{site.id}</span> - {site.name}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Preview Step */}
      {currentStep === "preview" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {t("batchUpload.preview.title")}
              </span>
              <div className="flex items-center gap-4 text-sm font-normal">
                <span className="flex items-center gap-1">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  {validCount} {t("batchUpload.preview.valid")}
                </span>
                <span className="flex items-center gap-1">
                  <XCircle className="h-4 w-4 text-red-500" />
                  {invalidCount} {t("batchUpload.preview.invalid")}
                </span>
              </div>
            </CardTitle>
            <CardDescription>
              {t("batchUpload.preview.description")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Error Report Download */}
            {invalidCount > 0 && (
              <div className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-red-500" />
                  <span className="text-sm">
                    {t("batchUpload.preview.rowsWithErrors", {
                      count: invalidCount,
                    })}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadErrorReport}
                >
                  {t("batchUpload.preview.downloadErrorReport")}
                </Button>
              </div>
            )}

            {/* Rows Table */}
            <div className="border rounded-lg overflow-hidden">
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-start">
                        {t("batchUpload.table.row")}
                      </th>
                      <th className="px-4 py-2 text-start">
                        {t("batchUpload.table.topic")}
                      </th>
                      <th className="px-4 py-2 text-start">
                        {t("batchUpload.table.site")}
                      </th>
                      <th className="px-4 py-2 text-start">
                        {t("batchUpload.table.keywords")}
                      </th>
                      <th className="px-4 py-2 text-start">
                        {t("batchUpload.table.words")}
                      </th>
                      <th className="px-4 py-2 text-start">
                        {t("batchUpload.table.status")}
                      </th>
                      <th className="px-4 py-2 text-start">
                        {t("batchUpload.table.actions")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedRows.map((row) => (
                      <tr
                        key={row.rowNumber}
                        className={cn(
                          "border-t",
                          !row.isValid && "bg-red-50 dark:bg-red-950/10",
                        )}
                      >
                        <td className="px-4 py-2">{row.rowNumber}</td>
                        <td
                          className="px-4 py-2 max-w-[200px] truncate"
                          title={row.topic}
                        >
                          {row.topic || (
                            <span className="text-red-500">
                              {t("batchUpload.preview.missing")}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          {row.site_name || (
                            <span className="text-red-500">
                              {t("batchUpload.preview.invalid")}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex flex-wrap gap-1">
                            {row.keywords.slice(0, 3).map((kw, i) => (
                              <Badge
                                key={i}
                                variant="secondary"
                                className="text-xs"
                              >
                                {kw}
                              </Badge>
                            ))}
                            {row.keywords.length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{row.keywords.length - 3}
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2">{row.target_word_count}</td>
                        <td className="px-4 py-2">
                          {row.isValid ? (
                            <Badge variant="default" className="bg-green-500">
                              {t("batchUpload.preview.valid")}
                            </Badge>
                          ) : (
                            <Badge
                              variant="destructive"
                              title={row.errors.join(", ")}
                            >
                              {t("batchUpload.preview.errorCount", {
                                count: row.errors.length,
                              })}
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeRow(row.rowNumber)}
                          >
                            <Trash2 className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between pt-4">
              <Button
                variant="outline"
                onClick={() => setCurrentStep("upload")}
              >
                <ChevronLeft className="me-2 h-4 w-4" />
                {t("app.back")}
              </Button>
              <Button
                onClick={() => setCurrentStep("submit")}
                disabled={validCount === 0}
              >
                {t("app.continue")}
                <ChevronRight className="ms-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Submit Step */}
      {currentStep === "submit" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Play className="h-5 w-5" />
              {t("batchUpload.submit.title")}
            </CardTitle>
            <CardDescription>
              {t("batchUpload.submit.description")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-muted rounded-lg text-center">
                <div className="text-3xl font-bold text-primary">
                  {validCount}
                </div>
                <div className="text-sm text-muted-foreground">
                  {t("batchUpload.submit.articlesToGenerate")}
                </div>
              </div>
              <div className="p-4 bg-muted rounded-lg text-center">
                <div className="text-3xl font-bold">
                  {parsedRows
                    .filter((r) => r.isValid)
                    .reduce((sum, r) => sum + r.target_word_count, 0)
                    .toLocaleString()}
                </div>
                <div className="text-sm text-muted-foreground">
                  {t("batchUpload.submit.totalWords")}
                </div>
              </div>
              <div className="p-4 bg-muted rounded-lg text-center">
                <div className="text-3xl font-bold">
                  {
                    new Set(
                      parsedRows.filter((r) => r.isValid).map((r) => r.site_id),
                    ).size
                  }
                </div>
                <div className="text-sm text-muted-foreground">
                  {t("batchUpload.submit.targetSites")}
                </div>
              </div>
            </div>

            {/* Priority Selection */}
            <div className="space-y-2">
              <Label>{t("batchUpload.submit.batchPriority")}</Label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((p) => (
                  <Button
                    key={p}
                    variant={batchPriority === p ? "default" : "outline"}
                    size="sm"
                    onClick={() => setBatchPriority(p)}
                  >
                    {p}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                {t("batchUpload.submit.priorityHint")}
              </p>
            </div>

            {/* Warning */}
            <div className="p-4 bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div>
                  <p className="font-medium text-yellow-800 dark:text-yellow-200">
                    {t("batchUpload.submit.warningTitle", {
                      count: validCount,
                    })}
                  </p>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                    {t("batchUpload.submit.warningDescription")}
                  </p>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between pt-4">
              <Button
                variant="outline"
                onClick={() => setCurrentStep("preview")}
              >
                <ChevronLeft className="me-2 h-4 w-4" />
                {t("app.back")}
              </Button>
              <Button onClick={submitBatch}>
                <Play className="me-2 h-4 w-4" />
                {t("batchUpload.submit.startBatch")}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Progress Step */}
      {currentStep === "progress" && batchProgress && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Loader2
                className={cn(
                  "h-5 w-5",
                  batchProgress.inProgress > 0 && "animate-spin",
                )}
              />
              {t("batchUpload.progress.title")}
            </CardTitle>
            <CardDescription>
              {batchProgress.completed + batchProgress.failed <
              batchProgress.total
                ? t("batchUpload.progress.processing")
                : t("batchUpload.progress.complete")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>{t("batchUpload.progress.progress")}</span>
                <span>{Math.round(progressPercent)}%</span>
              </div>
              <Progress value={progressPercent} className="h-3" />
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center p-3 bg-muted rounded-lg">
                <div className="text-2xl font-bold">{batchProgress.total}</div>
                <div className="text-xs text-muted-foreground">
                  {t("batchUpload.progress.total")}
                </div>
              </div>
              <div className="text-center p-3 bg-green-50 dark:bg-green-950/20 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {batchProgress.completed}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t("batchUpload.progress.completed")}
                </div>
              </div>
              <div className="text-center p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">
                  {batchProgress.inProgress}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t("batchUpload.progress.inProgress")}
                </div>
              </div>
              <div className="text-center p-3 bg-red-50 dark:bg-red-950/20 rounded-lg">
                <div className="text-2xl font-bold text-red-600">
                  {batchProgress.failed}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t("batchUpload.progress.failed")}
                </div>
              </div>
            </div>

            {/* Results List */}
            {batchProgress.results.length > 0 && (
              <div className="space-y-2">
                <h3 className="font-medium">
                  {t("batchUpload.progress.results")}
                </h3>
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {batchProgress.results.map((result, idx) => (
                    <div
                      key={idx}
                      className={cn(
                        "flex items-center justify-between p-2 rounded-lg",
                        result.articleId
                          ? "bg-green-50 dark:bg-green-950/20"
                          : "bg-red-50 dark:bg-red-950/20",
                      )}
                    >
                      <span className="text-sm">
                        {t("batchUpload.progress.row")} {result.rowNumber}:{" "}
                        {parsedRows
                          .find((r) => r.rowNumber === result.rowNumber)
                          ?.topic.slice(0, 50)}
                        {(parsedRows.find(
                          (r) => r.rowNumber === result.rowNumber,
                        )?.topic.length || 0) > 50 && "..."}
                      </span>
                      {result.articleId ? (
                        <Badge variant="default" className="bg-green-500">
                          <CheckCircle className="h-3 w-3 me-1" />
                          {t("batchUpload.progress.created")}
                        </Badge>
                      ) : (
                        <Badge variant="destructive">
                          <XCircle className="h-3 w-3 me-1" />
                          {t("batchUpload.progress.failed")}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Done Button */}
            {batchProgress.completed + batchProgress.failed ===
              batchProgress.total && (
              <div className="flex justify-center pt-4">
                <Button onClick={() => navigate("/articles")}>
                  {t("batchUpload.progress.viewAllArticles")}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
